import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope } from "@fortawesome/free-solid-svg-icons";
import { faPhone ,faLocationDot} from "@fortawesome/free-solid-svg-icons";
import { API } from "../../config";
import SearchableSelect from "./SearchableSelect";

const ZONE_API = `${import.meta.env.VITE_API_BASE_URL ?? "http://localhost:5000"}/api/v1`;

interface Course {
  course_id: number;
  course_name: string;
  record_status_id: number;
  display_order: number;
}

interface Zone {
  zone_id: number;
  zone_name: string;
}

interface ZoneArea {
  area_id: number;
  area_name: string;
}

type FieldErrors = {
  fullName?: string;
  email?: string;
  phone?: string;
  courseId?: string;
  submit?: string;
};

export default function RightSideActions() {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  const [form, setForm] = useState({
    fullName: "",
    email: "",
    phone: "",
    grade: "",
    location: "",
    centre: "",
    courseId: "",
    message: "",
  });
  const [courses, setCourses] = useState<Course[]>([]);
  const [zones, setZones] = useState<Zone[]>([]);
  const [areas, setAreas] = useState<ZoneArea[]>([]);
  const [selectedZoneId, setSelectedZoneId] = useState<number | null>(null);
  const [selectedAreaId, setSelectedAreaId] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  useEffect(() => {
    fetch(`${API}/courses`)
      .then((r) => r.json())
      .then((data) => {
        const active = (data.courses as Course[])
          .filter((c) => c.record_status_id === 1)
          .sort((a, b) => a.display_order - b.display_order);
        setCourses(active);
      })
      .catch(() => {});
    fetch(`${ZONE_API}/zones`)
      .then((r) => r.json())
      .then((d) => setZones(d.zones ?? []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedZoneId) { setAreas([]); return; }
    fetch(`${ZONE_API}/zone_areas/zone/${selectedZoneId}`)
      .then((r) => r.json())
      .then((d) => setAreas(d.areas ?? []))
      .catch(() => {});
  }, [selectedZoneId]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const name = e.target.name;
    let value = e.target.value;
    value = value.replace(/^\s+/, ""); // no leading spaces on any field
    if (name === "location") {
      const zone = zones.find((z) => z.zone_name === value);
      setSelectedZoneId(zone?.zone_id ?? null);
      setSelectedAreaId(null);
      setForm((prev) => ({ ...prev, location: value, centre: "" }));
      return;
    }
    if (name === "centre") {
      const area = areas.find((a) => a.area_name === value);
      setSelectedAreaId(area?.area_id ?? null);
      setForm((prev) => ({ ...prev, centre: value }));
      return;
    }
    if (name === "fullName") {
      value = value.replace(/[^a-zA-Z\s]/g, ""); // allow only letters and spaces
    }
    if (name === "email") {
      value = value.replace(/\s/g, ""); // no spaces anywhere in email
    }
    if (name === "phone") {
      value = value.replace(/[a-zA-Z]/g, ""); // no alphabets in phone
      if (value.length > 0 && !/^[6-9]/.test(value)) return; // first digit must be 6,7,8,9
      if (value.length > 10) return; // max 10 digits
    }
    setForm({ ...form, [name]: value });
    if (name in fieldErrors) {
      setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleNameBlur = () => {
    setForm((prev) => ({ ...prev, fullName: prev.fullName.trim() }));
  };

  const handleClose = () => {
    setIsOpen(false);
    setSuccess(false);
    setFieldErrors({});
    setForm({ fullName: "", email: "", phone: "", grade: "", location: "", centre: "", courseId: "", message: "" });
    setSelectedZoneId(null);
    setSelectedAreaId(null);
    setAreas([]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const errors: FieldErrors = {};
    if (!form.fullName.trim()) errors.fullName = "Enter a full name.";
    if (!form.email.trim()) {
      errors.email = "Please enter a valid email address.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      errors.email = "Please enter a valid email address.";
    }
    if (!form.phone.trim()) {
      errors.phone = "Enter a valid 10-digit number starting with 6, 7, 8 or 9.";
    } else if (!/^[6-9]\d{9}$/.test(form.phone.trim())) {
      errors.phone = "Enter a valid 10-digit number starting with 6, 7, 8 or 9.";
    }
    if (!form.courseId) errors.courseId = "Please select a course.";

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setFieldErrors({});
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          full_name: form.fullName.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          student_grade: form.grade.trim() || null,
          location: [form.location, form.centre].filter(Boolean).join(" - ") || null,
          course_id: Number(form.courseId),
          message: form.message.trim() || null,
          centre_area_id: selectedAreaId ?? null,
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        setFieldErrors({ submit: data.message || "Submission failed. Please try again." });
      } else {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
        event: "form_submit_success",
        // form_name: "contact_us",
        course_id: Number(form.courseId),
        location: [form.location, form.centre].filter(Boolean).join(" - ") || null,
        // centre_area_id: selectedAreaId ?? null,
        }); 
        setSuccess(true);
        setForm({ fullName: "", email: "", phone: "", grade: "", location: "", centre: "", courseId: "", message: "" });
        setSelectedZoneId(null);
        setSelectedAreaId(null);
        setAreas([]);
      }
    } catch {
      setFieldErrors({ submit: "Network error. Please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* RIGHT SIDE VERTICAL BUTTONS */}

      {/* Contact Us */}
      <button
        onClick={() => setIsOpen(true)}
        aria-label="Contact us"
        className="fixed right-0 top-135 top-170 z-40 bg-red-600 text-white px-2 md:px-3 py-2 rounded-l-lg shadow-lg hover:bg-red-700 transition"
        style={{ writingMode: "vertical-rl" }}
      >
        <FontAwesomeIcon icon={faEnvelope} className="text-white text-xl" aria-hidden="true" />
      </button>

      {/* Training for Schools */}
      <button
        onClick={() => navigate("/training-school")}
        className="topTraining fixed right-0 z-40 bg-white text-red-600 border border-red-600 px-2 md:px-3 py-2 rounded-l-lg shadow-lg hover:bg-red-50 transition"
        style={{
          writingMode: "vertical-rl",
        }}
      >
        Training
      </button>

      {/* WhatsApp */}
      <a
        href="https://wa.me/917305075771"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Chat with us on WhatsApp"
        className="fixed right-0 z-40 bg-green-500 text-white p-3 rounded-l-lg shadow-lg hover:bg-green-600 transition"
        style={{ top: "calc(50px + 18rem)" }}
      >
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488z"></path>
        </svg>
      </a>

      {/* Phone */}
      <a
        href="tel:+919884088958"
        aria-label="Call us"
        className="group fixed right-0 z-40 flex items-center bg-blue-500 text-white rounded-l-lg shadow-lg overflow-hidden transition-all duration-300"
        style={{ top: "calc(115px + 18rem)" }}
      >
        {/* Icon box */}
        <div className="w-[49px] h-[46px] flex items-center justify-center bg-blue-500 rounded-l-lg">
          <FontAwesomeIcon icon={faPhone} className="text-white text-lg" />
        </div>

        {/* Numbers container */}
        <div className="h-[45px] max-w-0 group-hover:max-w-[170px] overflow-hidden transition-all duration-300">
          <div className="flex flex-col text-sm leading-5 px-0 group-hover:px-3">
            <span className="opacity-0 group-hover:opacity-100 transition delay-100">
              +91 98840 88958
            </span>

            <span className="opacity-0 group-hover:opacity-100 transition delay-300">
              +91 90000 12345
            </span>
          </div>
        </div>
      </a>
      {/* OVERLAY */}
      {isOpen && (
        <div
          onClick={handleClose}
          className="fixed inset-0 bg-black/40 z-40"
        />
      )}

      {/* SIDE PANEL */}
      <div
        className={`fixed right-0 top-0 h-full w-96 bg-white shadow-2xl border-l-4 border-red-600 transform transition-transform duration-300 z-50 ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="p-6 h-full overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h3 className="center-fd-username1 headingcolor">Contact Us</h3>

            <button
              onClick={handleClose}
              aria-label="Close contact form"
              className="p-2 hover:bg-gray-100 rounded-full"
            >
              <span aria-hidden="true">✖</span>
            </button>
          </div>

          {success ? (
            <div className="text-center py-10">
              <div className="text-green-600 text-5xl mb-4">✓</div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Thank you for reaching out!</h3>
              <p className="text-gray-500 text-sm mb-6">We've received your enquiry and will get back to you shortly.</p>
              <button
                onClick={() => setSuccess(false)}
                className="bg-red-500 hover:bg-red-600 text-white text-sm font-semibold px-6 py-2 rounded-lg transition"
              >
                Submit Another
              </button>
            </div>
          ) : (
            <form className="space-y-4" onSubmit={handleSubmit}>
              {/* Full Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="fullName"
                  value={form.fullName}
                  onChange={handleChange}
                  onBlur={handleNameBlur}
                  placeholder="Enter your full name"
                  className={`w-full border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 outline-none ${fieldErrors.fullName ? "border-red-400" : "border-gray-300"}`}
                />
                {fieldErrors.fullName && (
                  <p className="text-red-500 text-xs mt-1">{fieldErrors.fullName}</p>
                )}
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="Enter your email"
                  className={`w-full border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 outline-none ${fieldErrors.email ? "border-red-400" : "border-gray-300"}`}
                />
                {fieldErrors.email && (
                  <p className="text-red-500 text-xs mt-1">{fieldErrors.email}</p>
                )}
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  placeholder="Enter your phone number"
                  className={`w-full border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 outline-none ${fieldErrors.phone ? "border-red-400" : "border-gray-300"}`}
                />
                {fieldErrors.phone && (
                  <p className="text-red-500 text-xs mt-1">{fieldErrors.phone}</p>
                )}
              </div>

              {/* Grade */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Student's Grade
                </label>
                <input
                  type="text"
                  name="grade"
                  value={form.grade}
                  onChange={handleChange}
                  placeholder="Enter your grade/class (e.g., Grade 5, 10th Standard)"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 outline-none"
                />
              </div>

              {/* Location */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Where are you from?
                </label>
                <SearchableSelect
                  options={zones.map((z) => ({ value: z.zone_name, label: z.zone_name }))}
                  value={form.location}
                  allowRawValue
                  placeholder="Select your zone"
                  searchPlaceholder="Search zone..."
                  sizeClass="px-3 py-2 rounded-md text-sm"
                  ringClass="ring-red-500"
                  onChange={(val) =>
                    handleChange({ target: { name: "location", value: String(val) } } as React.ChangeEvent<HTMLSelectElement>)
                  }
                />
              </div>

              {/* Select Centre */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select the Centre
                </label>
                {form.location === "Others" ? (
                  <input
                    type="text"
                    name="centre"
                    value={form.centre}
                    onChange={handleChange}
                    placeholder="Enter your centre or area"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 outline-none bg-gray-50"
                  />
                ) : (
                  <SearchableSelect
                    options={areas.map((a) => ({ value: a.area_name, label: a.area_name }))}
                    value={form.centre}
                    allowRawValue
                    disabled={!selectedZoneId}
                    placeholder="Please choose above zone"
                    searchPlaceholder="Search centre..."
                    sizeClass="px-3 py-2 rounded-md text-sm"
                    ringClass="ring-red-500"
                    onChange={(val) =>
                      handleChange({ target: { name: "centre", value: String(val) } } as React.ChangeEvent<HTMLSelectElement>)
                    }
                  />
                )}
              </div>

              {/* Course Interest */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Course Interest <span className="text-red-500">*</span>
                </label>
                <SearchableSelect
                  options={courses.map((c) => ({ value: c.course_id, label: c.course_name }))}
                  value={form.courseId}
                  error={!!fieldErrors.courseId}
                  placeholder="Select a course"
                  searchPlaceholder="Search course..."
                  sizeClass="px-3 py-2 rounded-md text-sm"
                  ringClass="ring-red-500"
                  onChange={(val) =>
                    handleChange({ target: { name: "courseId", value: String(val) } } as React.ChangeEvent<HTMLSelectElement>)
                  }
                />
                {fieldErrors.courseId && (
                  <p className="text-red-500 text-xs mt-1">{fieldErrors.courseId}</p>
                )}
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Additional Message
                </label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Tell us about your learning goals..."
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 outline-none resize-none"
                ></textarea>
              </div>

              {/* Submit error */}
              {fieldErrors.submit && (
                <p className="text-red-500 text-sm text-center">{fieldErrors.submit}</p>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-red-600 text-white py-3 rounded-md font-semibold hover:bg-red-700 transition disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? "Submitting..." : "Submit"}
              </button>

              <div className="mt-8 pt-6 border-t border-gray-200">
                <h4 className="text-gray-900 mb-4">Get in Touch</h4>
                <div className="space-y-3 text-sm text-gray-600">
                  <p><FontAwesomeIcon
                icon={faPhone}
                className="text-black text-sm px-2"
              />
               +91 98840 88958 | 98841 88158</p>
                  <p> <FontAwesomeIcon
                icon={faEnvelope}
                className="text-black text-sm px-2"
              />info@phonicspower.com</p>
                  <div>
                    <p><FontAwesomeIcon
                                    icon={faLocationDot}
                                    className="text-black text-sm px-2"
                                  /> Phonics Power Educational Services,</p>
                    <p className="ml-6"># 120, Silapathigaram Street,</p>
                    <p className="ml-6">Ganesh Nagar, Selaiyur,</p>
                    <p className="ml-6">Chennai - 600 073</p>
                  </div>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
    </>
  );
}
